import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import CloseIcon from "src/core/assets/images/icons/close.svg?react";
import { Spinner } from "src/core/components/custom-ui/spinner";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/core/components/ui/select";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { RISK_LEVEL_CLASSES } from "src/core/constants/risk";
import { cn } from "src/core/lib/utils";

const isRiskOption = (option: ISelectOption | IRiskOption): option is IRiskOption => {
  return "risk" in option;
};

type FormMultiSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  options: ISelectOption[] | IRiskOption[];
  placeholder?: string;
  required?: boolean;
  softRequired?: boolean;
  className?: string;
  control?: Control<T>;
  isLoading?: boolean;
  disabled?: boolean;
  withBadge?: boolean;
};

export function FormMultiSelect<T extends FieldValues>({
  name,
  label,
  options,
  placeholder,
  required = false,
  softRequired = false,
  className = "w-[360px]",
  control,
  isLoading = false,
  disabled = false,
  withBadge = false,
}: FormMultiSelectProps<T>) {
  const { t } = useTranslation();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selectedValues = (field.value as string[]) || [];
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;

        // Filtrer les options pour ne garder que celles non sélectionnées
        const availableOptions = options.filter((option) => !selectedValues.includes(option.value));

        const handleSelect = (value: string) => {
          if (!selectedValues.includes(value)) {
            field.onChange([...selectedValues, value]);
          }
        };

        const handleRemove = (valueToRemove: string) => {
          field.onChange(selectedValues.filter((value) => value !== valueToRemove));
        };

        return (
          <FormItem className={cn("min-w-0", className)}>
            <FormLabel required={required} className={cn(isInvalid && "text-alert")}>
              {label}
            </FormLabel>
            {isLoading ? (
              <div className="h-[40px] flex items-center">
                <Spinner />
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-y-10 min-w-0 w-full">
                  <Select
                    value=""
                    onValueChange={handleSelect}
                    disabled={isLoading || disabled || availableOptions.length === 0}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={cn(
                          "w-full select-wrapper",
                          isInvalid && "border-alert ring-alert",
                          showSoftRequired && "border-2 border-dashed border-orange-medium"
                        )}
                        data-testid={`multiselect-wrapper-${name}`}
                      >
                        <SelectValue
                          placeholder={placeholder || t("global.form.select.placeholder")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            {withBadge && isRiskOption(option) && option.risk && (
                              <div
                                className={cn(
                                  "min-w-[16px] h-[16px] rounded-full",
                                  RISK_LEVEL_CLASSES[option.risk]
                                )}
                              />
                            )}
                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Tags des éléments sélectionnés */}
                  {selectedValues.length > 0 && (
                    <div className="flex flex-row flex-wrap gap-x-10 gap-y-10 min-w-0 w-full">
                      {selectedValues.map((value) => {
                        const option = options.find((opt) => opt.value === value);
                        if (!option) return null;

                        return (
                          <div
                            key={value}
                            className="flex flex-row items-center gap-x-10 px-10 w-fit max-w-full min-w-0 h-20 rounded-lg border border-blue-completed font-light text-blue-completed text-xs"
                          >
                            {withBadge && isRiskOption(option) && option.risk && (
                              <div
                                className={cn(
                                  "shrink-0 min-w-[12px] h-[12px] rounded-full",
                                  RISK_LEVEL_CLASSES[option.risk]
                                )}
                              />
                            )}
                            <span className="truncate" title={option.label}>
                              {option.label}
                            </span>
                            <button
                              type="button"
                              className="shrink-0"
                              onClick={() => handleRemove(value)}
                              disabled={disabled}
                              aria-label={`remove ${option.label}`}
                            >
                              <CloseIcon className="h-10 w-10 text-blue-info" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <FormMessage />
                {showSoftRequired && (
                  <p className="font-light text-sm text-muted">
                    {t("global.form.warning.softRequired")}
                  </p>
                )}
              </>
            )}
          </FormItem>
        );
      }}
    />
  );
}
